import {useMemo} from 'react'
import {Loader2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {MutedText} from '@/components/shared/MutedText'
import type {PreviewResponse} from '@/types/inbound-connector'

/* eslint-disable i18next/no-literal-string -- file-extension constants, never user-visible */
function extensionForContentType(ct: string | null): string {
    if (!ct) return 'txt'
    const lower = ct.toLowerCase()
    if (lower.includes('json')) return 'json'
    if (lower.includes('xml')) return 'xml'
    if (lower.includes('html')) return 'html'
    if (lower.includes('csv')) return 'csv'
    return 'txt'
}
/* eslint-enable i18next/no-literal-string */

function tryPrettyPrint(body: string, contentType: string | null): string {
    if (!contentType || !contentType.toLowerCase().includes('json')) return body
    try {
        return JSON.stringify(JSON.parse(body), null, 2)
    } catch {
        return body
    }
}

export function PreviewResultDialog({connectorId, response, isLoading, onClose}: {
    connectorId: string
    response: PreviewResponse | null
    isLoading: boolean
    onClose: () => void
}) {
    const i18n = useI18n()
    const failed = response != null && !response.success
    const body = response?.body ?? ''
    const contentType = response?.contentType ?? null
    const pretty = useMemo(() => tryPrettyPrint(body, contentType), [body, contentType])

    const handleDownload = () => {
        // eslint-disable-next-line i18next/no-literal-string -- MIME type fallback, never user-visible
        const blob = new Blob([body], {type: contentType ?? 'text/plain'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `preview-${connectorId}.${extensionForContentType(contentType)}`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="preview-result-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.previewResultTitle}</DialogTitle>
                    <DialogDescription>
                        {isLoading && i18n.inboundConnectors.previewRunning}
                        {!isLoading && failed && (response?.errorMessage ?? i18n.inboundConnectors.previewFailed)}
                        {!isLoading && !failed && response && i18n.inboundConnectors.previewStatusLine({status: response.statusCode, contentType: contentType ?? '—'})}
                    </DialogDescription>
                </DialogHeader>
                <div className="overflow-auto flex-1 py-4">
                    {isLoading ? (
                        <div className="flex items-center gap-2 py-8 justify-center" data-testid="preview-loading">
                            <Loader2 className="h-4 w-4 animate-spin"/>
                            <MutedText>{i18n.inboundConnectors.previewWaitingForRecords}</MutedText>
                        </div>
                    ) : !body ? (
                        <MutedText className="block py-6 text-center" data-testid="preview-empty">
                            {failed && response?.errorMessage ? response.errorMessage : i18n.inboundConnectors.previewNoRecords}
                        </MutedText>
                    ) : (
                        <pre
                            className="text-xs whitespace-pre-wrap break-words bg-muted p-3 rounded overflow-auto"
                            data-testid="preview-body"
                        >
                            {pretty}
                        </pre>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleDownload}
                        disabled={isLoading || !body}
                        data-testid="btn-download-preview"
                    >
                        {i18n.inboundConnectors.downloadResponse}
                    </Button>
                    <Button type="button" size="sm" onClick={onClose} data-testid="btn-close-preview">
                        {i18n.common.close}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
